import { ArrowBackIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Heading,
  IconButton,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  DataSourceCode,
  DataSourceType,
} from "@syla/shared/types/models/DataSourceBase";
import { DataSourceWithNetworks } from "@syla/shared/types/responses/DataSourceWithNetworks";
import React, { useMemo, useRef, useEffect } from "react";
import { DataSourceInputType } from "../../../../helper/dataSource/DataSourceInputType";
import { CreateEmptyDataSourceButton } from "../components/CreateEmptyDataSourceButton";
import { DataSourceDrawerFooter } from "../components/DataSourceDrawerFooter";
import { DataSourceDrawHeader } from "../components/DataSourceDrawerHeader";
import { DataSourceTitle } from "../components/DataSourceTitle";
import { ImportMethodButton } from "../components/ImportMethodButton";
import { getImportMethodsAvailable } from "../helpers/getImportMethodsAvailable";
import { getSupportedImportMethods } from "../helpers/getSupportedImportMethods";
import { SkipForNowButton } from "../SelectFirstDataSource";

// Empty is used for the 'create empty data source' option.
export type ImportMethod = DataSourceInputType | "Empty";

export function ImportMethodSelectionPanel({
  dataSource,
  importMethods,
  customName,
  onSelect,
  onBack,
  enableSkipForNow,
}: {
  dataSource: DataSourceWithNetworks;
  importMethods: ImportMethod[];
  customName: string;
  onSelect: (method: ImportMethod) => void;
  onBack: () => void;
  enableSkipForNow?: boolean;
}): React.ReactElement {
  const optionButtonRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const importTypeOptions = useMemo(() => {
    return getImportMethodsAvailable(dataSource, importMethods);
  }, [dataSource, importMethods]);

  const recommended = getRecommendedImportMethod(dataSource);

  function focusOption(index: number): void {
    const optionCount = importTypeOptions.length;
    const wrappedIndex = (index + optionCount) % optionCount;

    optionButtonRefs.current[wrappedIndex]?.focus();
  }

  useEffect(() => {
    optionButtonRefs.current[0]?.focus();
  }, [importTypeOptions]);

  function handleOptionKeyDown(
    event: React.KeyboardEvent,
    currentIndex: number,
  ): void {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        focusOption(currentIndex + 1);
        break;

      case "ArrowUp":
        event.preventDefault();
        focusOption(currentIndex - 1);
        break;

      case "Home":
        event.preventDefault();
        focusOption(0);
        break;

      case "End":
        event.preventDefault();
        focusOption(importTypeOptions.length - 1);
        break;

      case "Escape":
        event.preventDefault();
        onBack();
        break;
    }
  }

  return (
    <>
      <DataSourceDrawHeader>
        <IconButton
          variant="ghost"
          aria-label="Back"
          icon={<ArrowBackIcon />}
          boxSize="2.5rem"
          fontSize="2xl"
          onClick={onBack}
        />
        <DataSourceTitle name={customName} image={dataSource.image} />
      </DataSourceDrawHeader>

      <VStack
        w="full"
        px={4}
        py={4}
        overflowY="auto"
        flex={1}
        align="flex-start"
        spacing={6}
      >
        {enableSkipForNow && <SkipForNowButton />}
        <Box w="full">
          <Heading as="h2" size="md">
            Choose import method
          </Heading>
          <Text>{subHeadingText(dataSource)}</Text>
        </Box>

        <VStack w="full" spacing={4} align="flex-start">
          {importTypeOptions.map((option, index) => {
            const commonProps = {
              buttonRef: (element: HTMLButtonElement | null) => {
                optionButtonRefs.current[index] = element;
              },
              onKeyDown: (event: React.KeyboardEvent) => {
                handleOptionKeyDown(event, index);
              },
            };

            if (option.importType === "Empty") {
              return (
                <CreateEmptyDataSourceButton
                  key={option.importType}
                  option={option}
                  onSelect={onSelect}
                  {...commonProps}
                />
              );
            }

            return (
              <ImportMethodButton
                key={option.importType}
                option={option}
                recommended={recommended === option.importType}
                onSelect={onSelect}
                {...commonProps}
              />
            );
          })}
        </VStack>
      </VStack>

      <DataSourceDrawerFooter>
        <Button variant="outline" colorScheme="gray" onClick={onBack}>
          Back
        </Button>
      </DataSourceDrawerFooter>
    </>
  );
}

function subHeadingText(dataSource: DataSourceWithNetworks): string {
  if (dataSource.type === DataSourceType.Exchange) {
    return `Choose how you'd like to import your transactions from ${dataSource.name}.`;
  }

  return "Choose how you'd like to import your transactions.";
}

// Only a few platforms prefer CSV, so we just manage this in code for now.
const DataSourcesRecommendingCsv = [
  DataSourceCode.Binance,
  DataSourceCode.Bitaroo,
  DataSourceCode.Bybit,
];

function getRecommendedImportMethod(
  dataSource: DataSourceWithNetworks,
): ImportMethod | undefined {
  if (dataSource.code && DataSourcesRecommendingCsv.includes(dataSource.code)) {
    return DataSourceInputType.Csv;
  }

  return getSupportedImportMethods(dataSource)[0];
}
